import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { PurchaseRequestDetailModal } from "./PurchaseRequestDetailModal";
import { Archive } from "lucide-react";

interface PurchaseRequest {
  id: string;
  listing_id: string;
  buyer_id: string;
  seller_id: string;
  quantity: number;
  total_price: number;
  delivery_method: string;
  pickup_date: string | null;
  pickup_time: string | null;
  shipping_address: string | null;
  status: string;
  proposed_pickup_date: string | null;
  proposed_pickup_time: string | null;
  archived_by_buyer: boolean;
  archived_by_seller: boolean;
  created_at: string;
  listing_title?: string;
  listing_image?: string | null;
  listing_category?: string | null;
  pickup_address?: string | null;
  other_user_name?: string;
  other_user_email?: string;
  other_user_avatar?: string | null;
}

type DirectionFilter = "all" | "received" | "sent";
type StatusFilter = "all" | "pending" | "accepted" | "denied" | "completed";

const PR_STORAGE_KEY = "purchase-requests-filters-v1";

export function PurchaseRequestsTab() {
  const { user } = useAuth();
  const [requests, setRequests] = useState<PurchaseRequest[]>([]);
  const initial = (() => {
    try {
      const saved = sessionStorage.getItem(PR_STORAGE_KEY);
      if (saved) return JSON.parse(saved) as { directionFilter: DirectionFilter; statusFilter: StatusFilter; showArchive: boolean };
    } catch { /* ignore */ }
    return null;
  })();
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>(initial?.directionFilter ?? "all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(initial?.statusFilter ?? "all");
  const [selectedRequest, setSelectedRequest] = useState<PurchaseRequest | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [showArchive, setShowArchive] = useState(initial?.showArchive ?? false);

  useEffect(() => {
    try {
      sessionStorage.setItem(PR_STORAGE_KEY, JSON.stringify({ directionFilter, statusFilter, showArchive }));
    } catch { /* ignore */ }
  }, [directionFilter, statusFilter, showArchive]);

  const fetchRequests = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from("purchase_requests")
      .select("*")
      .order("created_at", { ascending: false });

    if (error || !data) return;

    const listingIds = [...new Set(data.map((r: any) => r.listing_id))];
    const otherUserIds = [...new Set(data.map((r: any) => r.buyer_id === user.id ? r.seller_id : r.buyer_id))];

    const [{ data: listings }, { data: profiles }] = await Promise.all([
      supabase.from("listings").select("id, title, images, category, pickup_address").in("id", listingIds),
      supabase.from("profiles").select("id, full_name, avatar_url").in("id", otherUserIds),
    ]);

    const listingMap = new Map((listings || []).map((l: any) => [l.id, l]));
    const profileMap = new Map((profiles || []).map((p: any) => [p.id, p]));

    const enriched: PurchaseRequest[] = data.map((r: any) => {
      const isBuyer = r.buyer_id === user.id;
      const otherUserId = isBuyer ? r.seller_id : r.buyer_id;
      const otherProfile = profileMap.get(otherUserId);
      const listing = listingMap.get(r.listing_id);
      return {
        ...r,
        listing_title: listing?.title || "Annons",
        listing_image: listing?.images?.[0] || null,
        listing_category: listing?.category || null,
        pickup_address: listing?.pickup_address || null,
        other_user_name: otherProfile?.full_name || "Okänd",
        other_user_email: otherProfile?.full_name
          ? otherProfile.full_name.toLowerCase().replace(/\s+/g, ".") + "@email.se"
          : "",
        other_user_avatar: otherProfile?.avatar_url || null,
      };
    });

    setRequests(enriched);
  };

  useEffect(() => {
    fetchRequests();
  }, [user]);

  const filtered = requests.filter((r) => {
    if (!user) return false;
    const isSent = r.buyer_id === user.id;
    const isArchivedForMe = isSent ? r.archived_by_buyer : r.archived_by_seller;

    if (showArchive) return isArchivedForMe;
    if (isArchivedForMe) return false;

    if (directionFilter === "sent" && !isSent) return false;
    if (directionFilter === "received" && isSent) return false;
    if (statusFilter !== "all") {
      if (statusFilter === "pending" && r.status !== "pending" && r.status !== "proposed") return false;
      if (statusFilter === "accepted" && r.status !== "accepted") return false;
      if (statusFilter === "denied" && r.status !== "denied") return false;
      if (statusFilter === "completed" && r.status !== "completed") return false;
    }
    return true;
  });

  const statusBorder = (status: string) => {
    if (status === "accepted") return "border-primary";
    if (status === "denied") return "border-destructive";
    if (status === "completed") return "border-muted-foreground";
    return "border-border";
  };

  const getStatusBadge = (r: PurchaseRequest) => {
    if (r.status === "completed") return { label: "Slutförd", className: "bg-muted-foreground text-background" };
    if (r.status === "accepted") return { label: "Accepterad", className: "bg-primary text-primary-foreground" };
    if (r.status === "denied") return { label: "Nekad", className: "bg-destructive text-destructive-foreground" };
    if (r.status === "proposed") {
      const isBuyer = r.buyer_id === user?.id;
      if (isBuyer) return { label: "Väntar ditt svar", className: "bg-muted-foreground text-background" };
      return { label: "Förslag skickat", className: "bg-muted-foreground text-background" };
    }
    return null;
  };

  const directionOptions: { value: DirectionFilter; label: string }[] = [
    { value: "all", label: "Alla" },
    { value: "received", label: "Mottagna" },
    { value: "sent", label: "Skickade" },
  ];

  const statusOptions: { value: StatusFilter; label: string }[] = [
    { value: "all", label: "Alla" },
    { value: "pending", label: "Väntar på svar" },
    { value: "accepted", label: "Accepterade" },
    { value: "denied", label: "Nekade" },
    { value: "completed", label: "Slutförda" },
  ];

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      {/* Filters sidebar */}
      <div className="w-full space-y-4 lg:w-56 shrink-0">
        <div className="rounded-lg bg-muted/50 p-4">
          <p className="mb-2 text-sm font-semibold text-foreground">Typ</p>
          <div className="space-y-1">
            {directionOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setDirectionFilter(opt.value)}
                className={cn(
                  "block w-full rounded-md px-3 py-1.5 text-left text-sm transition-colors",
                  directionFilter === opt.value
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-muted"
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-lg bg-muted/50 p-4">
          <p className="mb-2 text-sm font-semibold text-foreground">Status</p>
          <div className="space-y-1">
            {statusOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                className={cn(
                  "block w-full rounded-md px-3 py-1.5 text-left text-sm transition-colors",
                  statusFilter === opt.value
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-muted"
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <Button
          variant={showArchive ? "default" : "outline"}
          className="w-full gap-2"
          onClick={() => setShowArchive(!showArchive)}
        >
          <Archive className="h-4 w-4" />
          {showArchive ? "Tillbaka" : "Visa arkiv"}
        </Button>
      </div>

      {/* Cards grid */}
      <div className="flex-1">
        {showArchive && (
          <p className="mb-4 text-sm font-medium text-muted-foreground">Arkiverade köpförslag</p>
        )}
        {filtered.length === 0 ? (
          <div className="rounded-lg border border-border bg-card p-8 text-center">
            <p className="text-muted-foreground">
              {showArchive ? "Inga arkiverade köpförslag." : "Inga köpförslag att visa."}
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((r) => {
              const isSent = r.buyer_id === user!.id;
              const statusBadge = getStatusBadge(r);
              return (
                <button
                  key={r.id}
                  onClick={() => { setSelectedRequest(r); setDetailOpen(true); }}
                  className={cn(
                    "relative rounded-lg border-2 bg-card p-4 text-left transition-shadow hover:shadow-md",
                    statusBorder(r.status),
                    showArchive && "opacity-75"
                  )}
                >
                  {/* Badges */}
                  <div className="absolute right-3 top-3 flex flex-col items-end gap-1">
                    <Badge variant="secondary" className="text-[10px]">
                      {isSent ? "Skickat" : "Mottaget"}
                    </Badge>
                    {statusBadge && (
                      <Badge className={cn("text-[10px] border-0", statusBadge.className)}>
                        {statusBadge.label}
                      </Badge>
                    )}
                  </div>

                  <p className="mb-1 font-semibold text-foreground pr-24 line-clamp-1">{r.listing_title}</p>
                  <p className="mb-3 text-xs text-muted-foreground">
                    Skickat {new Date(r.created_at).toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric" })}
                  </p>

                  <div className="mb-3 flex items-center justify-between">
                    <span className="font-medium text-foreground">{r.total_price.toLocaleString("sv-SE")} kr</span>
                    <span className="text-sm text-muted-foreground">x{r.quantity}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="h-10 w-10 shrink-0 rounded overflow-hidden bg-muted">
                      {r.listing_image ? (
                        <img src={r.listing_image} alt={r.listing_title} className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-muted-foreground text-xs">—</div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{r.listing_title}</p>
                      {r.listing_category && (
                        <p className="text-xs text-muted-foreground truncate">{r.listing_category}</p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <PurchaseRequestDetailModal
        open={detailOpen}
        onOpenChange={setDetailOpen}
        request={selectedRequest}
        currentUserId={user?.id || ""}
        onUpdated={fetchRequests}
      />
    </div>
  );
}
